import {
  Component,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { GameTable } from '../../model/game-table';
import { Event } from '../../model/event';
import { GenericAutocompleteComponent } from '../generic-autocomplete/generic-autocomplete.component';
import { DISTANCE_KM_OPTIONS, extractLocationDisplayName } from '../../utils/catalog-filters';
import { UserGeolocationService } from '../../services/user-geolocation.service';

export interface CatalogAdvancedFilterValues {
  tableName: string | null;
  gameName: string | null;
  eventName: string | null;
  location: string | null;
  organization: string | null;
  maxDistanceKm: number | null;
}

export type GameTableAdvancedFilterValues = CatalogAdvancedFilterValues;

@Component({
  selector: 'app-catalog-advanced-filter, app-game-table-advanced-filter',
  standalone: true,
  imports: [ReactiveFormsModule, GenericAutocompleteComponent],
  templateUrl: './catalog-advanced-filter.component.html',
  styleUrl: './catalog-advanced-filter.component.css',
})
export class CatalogAdvancedFilterComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private userGeolocation = inject(UserGeolocationService);
  private destroyRef = inject(DestroyRef);

  protected readonly distanceKmOptions = DISTANCE_KM_OPTIONS;
  protected readonly hasUserPosition = () => !!this.userGeolocation.position();

  protected tableFilterForm = this.formBuilder.group({
    tableName: this.formBuilder.control<string | null>(null),
    gameName: this.formBuilder.control<string | null>(null),
    eventName: this.formBuilder.control<string | null>(null),
    location: this.formBuilder.control<string | null>(null),
    organization: this.formBuilder.control<string | null>(null),
    maxDistanceKm: this.formBuilder.control<number | null>(null),
  });

  private _mode: 'table' | 'event' = 'table';
  private _allGameTables: GameTable[] = [];
  private _allEvents: Event[] = [];

  @Input()
  set mode(value: 'table' | 'event') {
    this._mode = value;
    this.buildAutocompleteOptions();
  }

  get mode(): 'table' | 'event' {
    return this._mode;
  }

  @Input()
  set allGameTables(tables: GameTable[] | null | undefined) {
    this._allGameTables = tables ?? [];
    this.buildAutocompleteOptions();
  }

  get allGameTables(): GameTable[] {
    return this._allGameTables;
  }

  @Input()
  set allEvents(events: Event[] | null | undefined) {
    this._allEvents = events ?? [];
    this.buildAutocompleteOptions();
  }

  get allEvents(): Event[] {
    return this._allEvents;
  }

  @Input() includePast: boolean = false;

  @Input()
  set initialValues(values: Partial<CatalogAdvancedFilterValues> | null | undefined) {
    if (values) {
      this.tableFilterForm.patchValue(values, { emitEvent: false });
    }
  }

  @Output() filterChange = new EventEmitter<CatalogAdvancedFilterValues>();
  @Output() includePastChange = new EventEmitter<boolean>();
  @Output() resetFilter = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  protected tableNameOptions: string[] = [];
  protected gameNameOptions: string[] = [];
  protected eventNameOptions: string[] = [];
  protected locationOptions: string[] = [];
  protected organizationOptions: string[] = [];

  protected readonly searchTableNameOptions = (query: string): Observable<string[]> =>
    of(this.searchInOptions(this.tableNameOptions, query));

  protected readonly searchGameNameOptions = (query: string): Observable<string[]> =>
    of(this.searchInOptions(this.gameNameOptions, query));

  protected readonly searchEventNameOptions = (query: string): Observable<string[]> =>
    of(this.searchInOptions(this.eventNameOptions, query));

  protected readonly searchLocationOptions = (query: string): Observable<string[]> =>
    of(this.searchInOptions(this.locationOptions, query));

  protected readonly searchOrganizationOptions = (query: string): Observable<string[]> =>
    of(this.searchInOptions(this.organizationOptions, query));

  protected readonly displayStringOption = (item: string): string => item;

  constructor() {
    effect(() => {
      const control = this.tableFilterForm.controls.maxDistanceKm;
      if (this.hasUserPosition()) {
        control.enable({ emitEvent: false });
        return;
      }
      control.reset(null, { emitEvent: false });
      control.disable({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.tableFilterForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.filterChange.emit(this.tableFilterForm.getRawValue());
      });
  }

  protected onFilterReset(): void {
    this.tableFilterForm.reset({
      tableName: null,
      gameName: null,
      eventName: null,
      location: null,
      organization: null,
      maxDistanceKm: null,
    });
    this.resetFilter.emit();
  }

  protected toggleIncludePast(): void {
    this.includePastChange.emit(!this.includePast);
  }

  protected onClose(): void {
    this.close.emit();
  }

  private buildAutocompleteOptions(): void {
    if (this._mode === 'event') {
      this.tableNameOptions = [];
      this.gameNameOptions = [];
      this.eventNameOptions = this.uniqueSorted(this._allEvents.map((event) => event.name));
      this.locationOptions = this.uniqueSorted(
        this._allEvents.map((event) => extractLocationDisplayName(event.location, '')),
      );
      this.organizationOptions = this.uniqueSorted(
        this._allEvents.map((event) => event.organization?.organizationName),
      );
      return;
    }

    this.tableNameOptions = this.uniqueSorted(this._allGameTables.map((table) => table.tableName));
    this.gameNameOptions = this.uniqueSorted(
      this._allGameTables.map((table) => table.playedGame?.gameName),
    );
    this.eventNameOptions = this.uniqueSorted(this._allGameTables.map((table) => table.event?.name));
    this.locationOptions = this.uniqueSorted(
      this._allGameTables.map((table) => this.extractLocationName(table)),
    );
    this.organizationOptions = this.uniqueSorted(
      this._allGameTables.map(
        (table) =>
          table.organization?.organizationName ?? table.event?.organization?.organizationName,
      ),
    );
  }

  private uniqueSorted(values: Array<string | undefined>): string[] {
    return [
      ...new Set(values.map((value) => (value ?? '').trim()).filter((value) => value.length > 0)),
    ].sort((first, second) => first.localeCompare(second, 'it'));
  }

  private extractLocationName(table: GameTable): string {
    return extractLocationDisplayName(table.location, 'Online');
  }

  private searchInOptions(options: string[], query: string): string[] {
    const normalizedQuery = (query ?? '').toLowerCase().trim();
    if (normalizedQuery.length === 0) {
      return options.slice(0, 20);
    }

    return options.filter((option) => option.toLowerCase().includes(normalizedQuery)).slice(0, 20);
  }
}
